import { defineStore } from 'pinia'
import { getServerSavedTop, saveTopGroupId } from '@/api';

const { localStorage } = window;

export const LS_KEY_TOKEN = "VK_TOKEN";
export const LS_KEY_TOKEN_EXPIRES = "VK_TOKEN_EXPIRES";

/**
 * The only field names we're interested in.
 * 
 * @see https://dev.vk.com/ru/reference/objects/group 
 */
const keysToKeep = new Set([
  "id",
  "name",
  "members_count",
  "screen_name",
  "type",
  "is_closed",
  "photo_50",
  "deactivated",
]);

/**
 * @typedef {Object} TopGroup
 * @property {Number} topGroupId group id
 * @property {Number} ts timestamp of the measure
 * @property {Boolean} updateBackend optional flag to POST to backend, too
 */

export const useAppStore = defineStore('app', {
  state: () => ({
    token: localStorage.getItem(LS_KEY_TOKEN),
    tokenExpires: Number(localStorage.getItem(LS_KEY_TOKEN_EXPIRES) ?? 0),
    topGroupId: 0,
    topGroupIdTs: 0,
    topGroupIdFixed: 0, // assigned once to track history loading
    startParams: {},
    errors: [],
    creationTimes: new Map(), // group_id -> timestamp (seconds)

    progress: 0,
    progressRequested: 0, // when bridge event just sent for another chunk
    backSteps: 0, // number of completed exec calls, each covers 25 * 64 = 1600 groups, but may return a little less
    backStepsLimit: 3, // max number of exec calls. Can be increased later by button
    
    isBridgeInitialized: false, // Bridge initialization state
    isBridgeActive: true, // false when app is in background on mobile, true when (re)enabled
    isMenuAdded: false,
    hasAppPermissions: false, // VK permissions present, can re-request token
    
    groups: [], // ordered desc by group.id, no dupe ids, newer replaces older
    groupsMap: new Map(), // id: groupObject
    minGroupId: 0,
    isGetMoreForward: false,

    // UI
    isFetchingBackward: false,
    isFetchingForward: false,
    isFindingTop: false,
  }),

  getters: {
    isFavorite: (state) => state.startParams?.vk_is_favorite === "1",
    progressChunkCount: (state) => 5 * state.backStepsLimit,
  },

  actions: {
    error(err) {
      const error = typeof err === "string" ? err : JSON.stringify(err);
      this.errors.push(error);
      // TODO: display errors and allow to switch them off or degrade over time.
    },

    /**
     * Check if store has topGroupId to start finding tip from.
     * and optionally request one from the backend.
     */
    async checkTopGroup() {
      if (!this.topGroupId) {
        console.log('checkTopGroup: no top id, will fetch from backend');
        // Only get from server if no fresh local top id
        const { group_id, timestamp } = await getServerSavedTop();
        this.topGroupId = group_id;
        this.topGroupIdTs = timestamp;
      };
    },

    /**
     * Save new tip to the store and optionally to backend
     * @param {TopGroup} topGroup 
     */
    async updateTopGroup(topGroup) {
      const { topGroupId, ts, updateBackend } = topGroup;

      this.topGroupId = topGroupId;
      this.topGroupIdTs = ts || Date.now();

      if (updateBackend) {
        await saveTopGroupId(topGroupId);
      }
    },

    addCreationTimes(timesArray) {
      this.$patch(state => {
        for (const [groupId, timestamp] of timesArray) {
          state.creationTimes.set(groupId, timestamp);
        }
      });
      console.log(`creationTimes now has ${this.creationTimes.size} items`);
    },

    /**
     * Add groups to the store.groups
     * @param {array} groupsArray 
     */
    addGroups(groupsArray) {
      this.$patch((state) => {
        // append to the Map, removing extra keys
        for (const group of groupsArray) {
          for (const key of Object.keys(group)) {
            if (!keysToKeep.has(key)) {
              // remove unused properties
              delete group[key];
            }
          }

          // add to Map by id key, this guarantees uniqueness
          state.groupsMap.set(group.id, group);
        }

        // update "groups" array
        const updatedGroups = Array.from(state.groupsMap.entries())
          .toSorted(([a], [b]) => b - a)
          .map(([, group]) => group);

        state.groups = updatedGroups;
      });
    },

    setStartParams(params) {
      this.startParams = { ...params }; //  TODO: make reactive?

      const tokenSettings = params.vk_access_token_settings ?? [];
      if (tokenSettings.includes("menu")) {
        this.isMenuAdded = true;
      }
      if (tokenSettings.includes("groups")) {
        this.hasAppPermissions = true;
      }
    },

  },
})
